import React, { useState, useEffect } from 'react';
import { userHasPermission } from '@shared/helpers/api/mantenedores';

const EditPermissionsModal = ({ 
    isOpen, 
    onClose, 
    usuario, 
    modulo, 
    moduloData,
    todosLosPermisos, 
    onUpdatePermission,
    loading,
    canAdminUsers // NUEVO PROP
}) => {
    const [permissions, setPermissions] = useState({});
    const [hasChanges, setHasChanges] = useState(false);

    // Inicializar permisos cuando se abre el modal
    useEffect(() => {
        if (isOpen && usuario && modulo) {
            const initialPermissions = {};
            todosLosPermisos.forEach(permiso => {
                initialPermissions[permiso.permiso] = userHasPermission(usuario, modulo, permiso.permiso);
            });
            setPermissions(initialPermissions);
            setHasChanges(false);
        }
    }, [isOpen, usuario, modulo, todosLosPermisos]);

    // Manejar cambio en checkbox
    const handlePermissionChange = (permisoName, checked) => {
        const newPermissions = { ...permissions };
        
        // Si se activa "administrar", activar todos los demás
        if (permisoName === 'administrar' && checked) {
            todosLosPermisos.forEach(p => {
                newPermissions[p.permiso] = true;
            });
        }
        // Si se desactiva "administrar", desactivar todos
        else if (permisoName === 'administrar' && !checked) {
            todosLosPermisos.forEach(p => {
                newPermissions[p.permiso] = false;
            });
        }
        // Si se activa cualquier permiso cuando "administrar" está inactivo
        else {
            newPermissions[permisoName] = checked;
            
            // Si se desactiva cualquier permiso, desactivar "administrar"
            if (!checked) {
                newPermissions['administrar'] = false;
            }
            // Si se activan todos los permisos manualmente, activar "administrar"
            else if (todosLosPermisos.every(p => p.permiso === 'administrar' || newPermissions[p.permiso])) {
                newPermissions['administrar'] = true;
            }
        }
        
        setPermissions(newPermissions);
        setHasChanges(true);
    };

    // Guardar cambios
    const handleSave = async () => {
        if (!hasChanges) {
            onClose();
            return;
        }

        // Comparar permisos actuales vs nuevos y generar acciones
        const actions = [];
        
        todosLosPermisos.forEach(permiso => {
            const hadPermission = userHasPermission(usuario, modulo, permiso.permiso);
            const hasPermission = permissions[permiso.permiso];
            
            if (hadPermission && !hasPermission) {
                actions.push({ type: 'remove', permiso: permiso.permiso });
            } else if (!hadPermission && hasPermission) {
                actions.push({ type: 'add', permiso: permiso.permiso });
            }
        });

        // Ejecutar acciones
        for (const action of actions) {
            await onUpdatePermission(usuario.id, modulo, action.permiso, action.type);
        }

        onClose();
    };

    // Estilos
    const modalOverlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
    };

    const modalStyle = {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)'
    };

    const permissionItemStyle = {
        display: 'flex',
        alignItems: 'center',
        padding: '12px',
        borderRadius: '8px',
        marginBottom: '8px',
        border: '1px solid #e9ecef',
        backgroundColor: '#f8f9fa'
    };

    const checkboxStyle = {
        marginRight: '12px',
        transform: 'scale(1.2)'
    };

    if (!isOpen) return null;

    // Ordenar permisos: "administrar" siempre al final
    const permisosOrdenados = [
        ...todosLosPermisos.filter(p => p.permiso !== 'administrar'),
        ...todosLosPermisos.filter(p => p.permiso === 'administrar')
    ];

    return (
        <div style={modalOverlayStyle} onClick={onClose}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ margin: '0 0 8px 0' }}>
                        Editar Permisos - {moduloData?.descripcion}
                    </h3>
                    <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                        Usuario: <strong>{usuario?.nombre}</strong> ({usuario?.email})
                    </p>
                </div>

                <div style={{ marginBottom: '24px' }}>
                    {permisosOrdenados.map(permiso => {
                        const isChecked = permissions[permiso.permiso] || false;
                        const isAdministrar = permiso.permiso === 'administrar';
                        return (
                            <div 
                                key={permiso.id} 
                                style={{
                                    ...permissionItemStyle,
                                    backgroundColor: isAdministrar 
                                        ? (isChecked ? '#e8eaf6' : '#f8f9fa')
                                        : (isChecked ? '#e8f5e8' : '#f8f9fa'),
                                    border: isAdministrar 
                                        ? (isChecked ? '2px solid #3f51b5' : '1px solid #e9ecef')
                                        : '1px solid #e9ecef'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => handlePermissionChange(permiso.permiso, e.target.checked)}
                                    style={checkboxStyle}
                                    disabled={loading || (isAdministrar && !canAdminUsers())}
                                />
                                <div style={{ flex: 1 }}>
                                    <div style={{ 
                                        fontWeight: isAdministrar ? 'bold' : '500',
                                        marginBottom: '4px',
                                        textTransform: 'capitalize'
                                    }}>
                                        {isAdministrar && '👑 '}{permiso.permiso}
                                        {isAdministrar && ' (Acceso Total)'}
                                    </div>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        color: '#666' 
                                    }}>
                                        {permiso.descripcion}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {permissions['administrar'] && (
                    <div style={{
                        padding: '12px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        fontSize: '14px',
                        color: '#1565c0'
                    }}>
                        ℹ️ <strong>Administrador:</strong> Este usuario tendrá acceso completo a todas las funcionalidades del módulo.
                    </div>
                )}

                <div style={{ 
                    display: 'flex', 
                    gap: '12px', 
                    justifyContent: 'flex-end' 
                }}>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        style={{
                            padding: '10px 20px',
                            border: '1px solid #ddd',
                            backgroundColor: 'white',
                            borderRadius: '6px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={loading || !hasChanges}
                        style={{
                            padding: '10px 20px',
                            border: 'none',
                            backgroundColor: loading || !hasChanges ? '#ccc' : '#4CAF50',
                            color: 'white',
                            borderRadius: '6px',
                            cursor: loading || !hasChanges ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}
                    >
                        {loading ? '🔄 Guardando...' : 'Guardar Cambios'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditPermissionsModal;
